import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import host from "../../global.jsx";
import { Mail, RefreshCw, X, User, Building2, Copy, Check, Phone } from "lucide-react";

// TEMP: dummy data used only while the backend/IMAP credentials aren't wired up yet.
// Remove this block (and its usage in the catch blocks below) once the support-inbox
// endpoints work end-to-end.
const DUMMY_SUPPORT_INBOX = {
  mailbox: "support@geniehire.ai",
  unread_count: 4,
  emails: [
    {
      uid: "dummy-1",
      subject: "Interview Feedback - 68abc1234567890def12345",
      sender: "Rahul Sharma <rahul.sharma@example.com>",
      date: "Wed, 09 Jul 2026 09:12:00 +0530",
      candidate_info: {
        interview_id: "68abc1234567890def12345",
        candidate_name: "Rahul Sharma",
        candidate_email: "rahul.sharma@example.com",
        candidate_contact_number: "+91 98765 43210",
        job_role: "Backend Engineer",
        org_id: "64f1a2b3c4d5e6f7a8b9c0d1",
        organization_name: "Acme Technologies",
      },
    },
    {
      uid: "dummy-2",
      subject: "Refund request for Trial plan",
      sender: "Priya Nair <priya.nair@example.com>",
      date: "Wed, 09 Jul 2026 08:47:00 +0530",
      candidate_info: null,
    },
    {
      uid: "dummy-3",
      subject: "Re: Candidate feedback not submitting",
      sender: "Alex Johnson <alex.johnson@example.com>",
      date: "Tue, 08 Jul 2026 21:30:00 +0530",
      candidate_info: null,
    },
    {
      uid: "dummy-4",
      subject: "Question about org admin permissions",
      sender: "HR Team <hr.team@example.com>",
      date: "Tue, 08 Jul 2026 15:05:00 +0530",
      candidate_info: null,
    },
  ],
};

const DUMMY_EMAIL_BODY =
  "Hi Support Team,\n\nI'm reaching out regarding interview ID: 68abc1234567890def12345\n\nI wasn't able to submit my feedback form after completing the interview. The page kept loading indefinitely. Could you please help me resolve this?\n\nThanks,\nRahul";

// Click-to-copy text pill. `id` must be unique across the page so only the clicked
// instance shows the "copied" checkmark.
const CopyableText = ({ id, value, copiedId, onCopy, placeholder = "—", className = "" }) => {
  if (!value) {
    return <span className="text-gray-500">{placeholder}</span>;
  }
  const isCopied = copiedId === id;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onCopy(id, value);
      }}
      title="Click to copy"
      className={`inline-flex items-center gap-1.5 group/copy text-left hover:text-blue-300 transition-colors ${className}`}
    >
      <span className="truncate">{value}</span>
      {isCopied ? (
        <Check className="w-3.5 h-3.5 text-green-400 shrink-0" />
      ) : (
        <Copy className="w-3.5 h-3.5 shrink-0 opacity-0 group-hover/copy:opacity-70 transition-opacity" />
      )}
    </button>
  );
};

const SupportInbox = () => {
  const [emails, setEmails] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mailbox, setMailbox] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [usingDummyData, setUsingDummyData] = useState(false);

  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const accessToken = sessionStorage.getItem("accessToken");

  const handleCopy = useCallback((id, value) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedId(id);
        setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1500);
      })
      .catch((err) => console.error("Failed to copy:", err));
  }, []);

  const getUnreadEmails = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      setUsingDummyData(false);
      const response = await axios.get(`${host}/geniehire/support-inbox/unread`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.data) {
        setEmails(response.data.emails || []);
        setUnreadCount(response.data.unread_count || 0);
        setMailbox(response.data.mailbox || "");
      }
    } catch (error) {
      console.error("Error fetching support inbox, falling back to dummy data:", error);
      // TEMP: fall back to dummy data instead of showing an error, so the page is testable
      // before IMAP credentials / the backend endpoint are fully wired up.
      setEmails(DUMMY_SUPPORT_INBOX.emails);
      setUnreadCount(DUMMY_SUPPORT_INBOX.unread_count);
      setMailbox(DUMMY_SUPPORT_INBOX.mailbox);
      setUsingDummyData(true);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    getUnreadEmails();
  }, [getUnreadEmails]);

  const openEmail = async (email) => {
    setSelectedEmail({ ...email, body: null });
    setIsDetailLoading(true);
    setDetailError("");

    try {
      const response = await axios.get(
        `${host}/geniehire/support-inbox/email/${encodeURIComponent(email.uid)}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      setSelectedEmail(response.data);
    } catch (error) {
      console.error("Error fetching email detail, falling back to dummy body:", error);
      // TEMP: fall back to dummy body content until the detail endpoint is fully wired up.
      setSelectedEmail({
        ...email,
        body: DUMMY_EMAIL_BODY,
      });
      setDetailError("");
    } finally {
      setIsDetailLoading(false);
    }
  };

  const closeEmail = () => setSelectedEmail(null);

  return (
    <div className="min-h-screen py-4 px-12 mb-10 glass-effect">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-3">
            <Mail className="w-6 h-6" />
            Support Inbox
          </h1>
          {mailbox && (
            <p className="text-gray-400 text-sm mt-1">{mailbox}</p>
          )}
        </div>
        <button
          onClick={getUnreadEmails}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-transparent text-white rounded-full text-sm hover:bg-white/10 transition-colors border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {usingDummyData && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-6 text-center text-yellow-300 text-sm">
          Showing sample data — could not reach the support inbox API. Remove DUMMY_SUPPORT_INBOX once the backend is fully configured.
        </div>
      )}

      {/* Summary */}
      <div className="bg-white/10 p-6 rounded-lg shadow-lg mb-8 border border-gray-600/30 backdrop-blur-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-600/50 backdrop-blur-sm">
            <div className="text-gray-300 font-medium text-sm">Unread Emails</div>
            <div className="text-2xl font-bold text-white">{unreadCount}</div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64 w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : errorMessage ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center text-red-300">
          {errorMessage}
        </div>
      ) : emails.length === 0 ? (
        <div className="bg-white/10 rounded-lg shadow-lg border border-gray-600/30 backdrop-blur-sm p-8 text-center text-gray-300">
          No unread emails in the support inbox.
        </div>
      ) : (
        <div className="bg-white/10 rounded-lg shadow-lg overflow-hidden border border-gray-600/30 backdrop-blur-sm">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-800/30">
                  <th className="border-b p-4 text-left text-white">Subject</th>
                  <th className="border-b p-4 text-left text-white">From</th>
                  <th className="border-b p-4 text-left text-white whitespace-nowrap">Date</th>
                  <th className="border-b p-4 text-left text-white whitespace-nowrap">Candidate</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((email, index) => (
                  <tr
                    key={email.uid || index}
                    onClick={() => openEmail(email)}
                    className="hover:bg-gray-800/20 cursor-pointer"
                  >
                    <td className="border-b p-4 text-white">{email.subject || "(No subject)"}</td>
                    <td className="border-b p-4 text-gray-200">{email.sender || "Unknown sender"}</td>
                    <td className="border-b p-4 text-gray-200 whitespace-nowrap">{email.date || "N/A"}</td>
                    <td className="border-b p-4 text-gray-200 whitespace-nowrap">
                      {email.candidate_info?.candidate_name || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Email Detail Modal */}
      {selectedEmail && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-70"
            onClick={closeEmail}
            style={{ backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
          ></div>
          <div
            className="glass-effect rounded-lg p-6 w-full max-w-2xl mx-auto shadow-lg overflow-y-auto max-h-[80vh] relative"
            style={{
              background: "linear-gradient(to bottom right, rgba(20,30,58,0.97), rgba(11,10,31,0.97))",
              backdropFilter: "blur(40px)",
              WebkitBackdropFilter: "blur(50px)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "10px",
            }}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">{selectedEmail.subject || "(No subject)"}</h3>
                <p className="text-gray-400 text-sm mt-1">{selectedEmail.sender}</p>
                <p className="text-gray-500 text-xs mt-1">{selectedEmail.date}</p>
              </div>
              <button onClick={closeEmail}>
                <X className="w-5 h-5 text-white cursor-pointer hover:text-gray-300" />
              </button>
            </div>

            <hr className="border-gray-600 mb-4" />

            {selectedEmail.candidate_info && (
              <div className="bg-gray-800/30 rounded-lg border border-gray-600/50 p-4 mb-4 space-y-2">
                <div className="flex items-center gap-2 text-white text-sm font-medium">
                  <User className="w-4 h-4" />
                  {selectedEmail.candidate_info.candidate_name || "Unknown candidate"}
                </div>
                {selectedEmail.candidate_info.candidate_email && (
                  <div className="text-gray-300 text-sm">
                    <CopyableText
                      id="modal-email"
                      value={selectedEmail.candidate_info.candidate_email}
                      copiedId={copiedId}
                      onCopy={handleCopy}
                    />
                  </div>
                )}
                {selectedEmail.candidate_info.candidate_contact_number && (
                  <div className="flex items-center gap-2 text-gray-300 text-sm">
                    <Phone className="w-4 h-4" />
                    <CopyableText
                      id="modal-contact"
                      value={selectedEmail.candidate_info.candidate_contact_number}
                      copiedId={copiedId}
                      onCopy={handleCopy}
                    />
                  </div>
                )}
                {selectedEmail.candidate_info.job_role && (
                  <div className="text-gray-300 text-sm">Role: {selectedEmail.candidate_info.job_role}</div>
                )}
                {selectedEmail.candidate_info.organization_name && (
                  <div className="flex items-center gap-2 text-gray-300 text-sm">
                    <Building2 className="w-4 h-4" />
                    {selectedEmail.candidate_info.organization_name}
                  </div>
                )}
                <div className="text-gray-500 text-xs font-mono flex items-center gap-1.5">
                  Interview ID:{" "}
                  <CopyableText
                    id="modal-interview-id"
                    value={selectedEmail.candidate_info.interview_id}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />
                </div>
              </div>
            )}

            {isDetailLoading ? (
              <div className="flex justify-center items-center h-32 w-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : detailError ? (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center text-red-300 text-sm">
                {detailError}
              </div>
            ) : (
              <div className="text-gray-200 text-sm whitespace-pre-wrap leading-relaxed">
                {selectedEmail.body}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportInbox;
